import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { FixedSizeGrid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { Scrollbars } from "react-custom-scrollbars";
import { isMobile } from 'react-device-detect';

import { HighlightSpan } from './PriceBoardTotalMode';

import { addFavSymbol, removeFavSymbol, changeSelectShowInfo, showPopupInfo, removeFindSymbol } from '../redux/actions/sharedActions';

import { formatNumber_N1, formatQtty, priceToNumber, formatNumber } from '../helpers/utils';

const minContainerWidth = 800;
const boxWidth = 250;

// ===== GRID BOX =====
export default memo(function PriceBoardBoxMode2(props) {

    const { symbolInfos } = props;
    //
    const dispatch = useDispatch();
    const findSymbol = useSelector(state => state.gShared.findSymbol);
    //
    const refTable = useRef(null) ;
    const refScroll = useRef(null);

    //
    const _handleScroll = useCallback(({ target }) => {
        const { scrollTop, scrollLeft } = target;
        if(refTable.current) {
            refTable.current.scrollTo({scrollLeft: scrollLeft, scrollTop: scrollTop});
        }
    }, [refTable.current]) 

    const _handleTableScroll = useCallback(({ horizontalScrollDirection, scrollLeft, scrollTop, scrollUpdateWasRequested, verticalScrollDirection }) => {
        if(refScroll.current) {
            refScroll.current.scrollLeft(scrollLeft);
            refScroll.current.scrollTop(scrollTop);
        }
        if(findSymbol && findSymbol.code && findSymbol.code !== "") {
            removeFindSymbol(dispatch, "");
        }
    }, [refScroll.current])

    const _scrollToSymbol = () => {
        if(refTable.current) {
            if(findSymbol && findSymbol.code && findSymbol.code !== "" && symbolInfos && symbolInfos.length > 0) {
                const _idx = symbolInfos.findIndex(e => e.code === findSymbol.code);
                if(_idx >= 0) {
                    if(refTable.current) {
                        const _tbNumberCols = Math.floor(refTable.current.props.width / boxWidth);
                        const _elRowIdx = Math.floor(_idx / _tbNumberCols);
                        const _elColIdx = _idx - (_tbNumberCols * _elRowIdx);
                        refTable.current.scrollToItem({
                            align: "start",
                            columnIndex: _elColIdx,
                            rowIndex: _elRowIdx
                        });
                    }
                }
            }
        }
        else {
            setTimeout(() => {
                _scrollToSymbol();
            }, 100);
        }
    }

    useEffect(() => {
        _scrollToSymbol();
    }, [findSymbol.counter, symbolInfos.length])

    //
    return (
        <div className={'price-board '}>
            {
                symbolInfos.length > 0  ? (
                    <AutoSizer>
                        {({ height, width }) => {
                            const _width = width < boxWidth ? boxWidth : width;
                            const _gridColumns = Math.floor(_width / boxWidth);
                            const _gridColumnWidth = _width / _gridColumns;
                            const _gridRows = Math.ceil(symbolInfos.length / _gridColumns);
                            //
                            return (
                                <Scrollbars
                                    ref={refScroll}
                                    autoHide={true}
                                    autoHeight={true}
                                    autoHeightMax={height}
                                    onScroll={_handleScroll}
                                    style={{ width: width}}
                                    renderTrackHorizontal={props => <div {...props} className="scroll-track-horizontal"/>}
                                    renderTrackVertical={props => <div {...props} className="scroll-track-vertical"/>}
                                    renderThumbHorizontal={props => <div {...props} className="scroll-thumb-horizontal"/>}
                                    renderThumbVertical={props => <div {...props} className="scroll-thumb-vertical"/>}
                                    renderView={props => <div {...props} className="scroll-view"/>}
                                    className={"scroll-container"}
                                > 
                                    <FixedSizeGrid
                                        ref={refTable}
                                        width={_width}
                                        height={height}
                                        columnCount={_gridColumns}
                                        columnWidth={_gridColumnWidth}
                                        rowCount={_gridRows}
                                        rowHeight={130}
                                        style={{ overflow: false }}
                                        onScroll={_handleTableScroll}
                                        overscanColumnCount={6}
                                        overscanRowCount={6}
                                    >
                                        {({ columnIndex, rowIndex, style }) => {
                                            const _itemIdx = (rowIndex * _gridColumns) + columnIndex;
                                            //
                                            if(_itemIdx >= symbolInfos.length) {
                                                return (
                                                    <div className={"dflex"} style={{...style, padding: 2}}> </div>
                                                )
                                            }
                                            else {
                                                const _symbolInfo = symbolInfos[(rowIndex * _gridColumns) + columnIndex];
                                                return (
                                                    <div className={"dflex"} style={{...style, padding: 2}}>
                                                        <PriceBoardBox 
                                                            className="price-board-grid-box"
                                                            style={{flex: 1}}
                                                            code={_symbolInfo.code}
                                                            counter={_symbolInfo.counter}
                                                        />
                                                    </div>
                                                )
                                            }
                                        }}
                                    </FixedSizeGrid>
                                </Scrollbars>
                            )
                        }}
                    </AutoSizer>
                ) : <></>
            }
        </div>
    )
})

// ===== BOX =====
const PriceBoardBox = memo(function(props) {

    const { code } = props;
    
    //
    const prevSymbolCode = useRef("");
    const prevIsOddlot = useRef(null);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const tabIsFocus = useSelector(state => state.gShared.tabIsFocus);
    const counter = useSelector(state => state.gSymbol.symbolInfos.find((e) => e.code === code).counter);
    const symbolInfo = useSelector(state => state.gSymbol.symbolInfos.find((e) => e.code === code));
    const isFav = useSelector(state => state.gShared.priceBoardFavSymbols.indexOf(symbolInfo.code) >= 0);

    const priceBoardIsOddlot = useSelector(state => state.gShared.priceBoardIsOddlot);

    const _fireEvent = prevSymbolCode.current === symbolInfo.code && tabIsFocus && prevIsOddlot.current == priceBoardIsOddlot;

    //
    const _renderHeaderBox = () => {
        if(isFav) {
            return <span onClick={() => _removeFavSymbol(symbolInfo.code)} style={{cursor: 'pointer', lineHeight: 0}}><img id={symbolInfo.code + "_star"} className="imgstar" src="/static/img/icon/icon-star-yellow.svg" alt="img" />&nbsp;</span>;
        }
        return <span onClick={() => _addFavSymbol(symbolInfo.code)} style={{cursor: 'pointer', lineHeight: 0}}><img id={symbolInfo.code + "_star"} className="imgstar" src="/static/img/icon/icon-star.svg" alt="img" />&nbsp;</span>;
    }


    //
    const _addFavSymbol = useCallback((code) => {
        addFavSymbol(dispatch, code);
    }, []);

    const _removeFavSymbol = useCallback((code) => {
        removeFavSymbol(dispatch, code);
    }, []);

    const _handleRowClick = useCallback((event, code) => {
        if(code) {
            if(code + "_code" === event.target.id) {
                showPopupInfo(dispatch, "ST", code);
            }
            else if(code + "_star" === event.target.id) {
                //
            }
            else {
                changeSelectShowInfo(dispatch, "ST", code);
            }
        }
    }, []); 

    //
    useEffect(() => {
        prevSymbolCode.current = symbolInfo.code;
    }, [symbolInfo.code]);

    useEffect(() => {
        prevIsOddlot.current = priceBoardIsOddlot;
    }, [priceBoardIsOddlot]);

    //
    return (
        <div className={props.className} style={{...props.style}} onClick={(event)=> _handleRowClick(event, symbolInfo.code)}>
            <table>
                {priceBoardIsOddlot ? (
                    <>
                    <tbody>
                        <tr>
                            <td colSpan={2} className={symbolInfo.matchPriceOdd_color}>
                                <div style={{width: '100%', display: 'flex', alignItems: 'center'}} >
                                    {_renderHeaderBox()}
                                    <span id={symbolInfo.code + "_code"} style={{cursor: 'pointer', fontWeight: 'bolder'}}>{symbolInfo.code}{(symbolInfo.referenceStatus && symbolInfo.referenceStatus !== '' && symbolInfo.referenceStatus !== '0' ? "*" : "")}</span>
                                    <div className='flex-1 text-right'>
                                        <HighlightSpan style={{fontWeight: 'bolder',fontSize: '18px',lineHeight: '21px'}} color={symbolInfo.matchPriceOdd_color} id={symbolInfo.code + "_matchPriceOdd"} value={symbolInfo.matchPriceOdd} fireEvent={_fireEvent} /> 
                                        <span className="fs-14"> (</span>
                                        <HighlightSpan style={{fontSize: '14px',lineHeight: '18px'}} color={symbolInfo.matchPriceOdd_color} id={symbolInfo.code + "_changePriceOdd"} value={formatNumber_N1(symbolInfo.changePriceOdd)} fireEvent={false} />
                                        <span className="fs-14">/</span>
                                        <HighlightSpan style={{fontSize: '14px',lineHeight: '18px'}} color={symbolInfo.matchPriceOdd_color} id={symbolInfo.code + "_pctChangePriceOdd"} value={formatNumber_N1(symbolInfo.pctChangePriceOdd)} fireEvent={false} />
                                        <span className="fs-14">%)</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span className="cl_flatlist">{t("tongKLKhop")}: </span> 
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_totalTradedQttyOdd"} value={priceToNumber(symbolInfo.totalTradedQttyOdd) === 0 ? '-' : formatNumber(symbolInfo.totalTradedQttyOdd)} fireEvent={_fireEvent} />
                            </td>
                            <td>
                                <span className="cl_flatlist">{t("klKhop")}: </span> 
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.matchPriceOdd_color}  id={symbolInfo.code + "_matchQttyOdd"} value={priceToNumber(symbolInfo.matchQttyOdd) === 0 ? '-' : formatQtty(symbolInfo.matchQttyOdd)} fireEvent={_fireEvent} />
                            </td>
                        </tr>
                        <tr>
                            <td className="cl_flatlist">{t("muaBid")}</td>
                            <td className="cl_flatlist">{t("banAsk")}</td>
                        </tr>
                        <tr>
                            <td>
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.bidPriceOdd1_color} id={symbolInfo.code + "_bidPriceOdd1"} value={priceToNumber(symbolInfo.bidPriceOdd1) === 0 ? '-' : formatNumber_N1(symbolInfo.bidPriceOdd1)} fireEvent={_fireEvent} />
                                <span> | </span>
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_bidQttyOdd1"} value={priceToNumber(symbolInfo.bidQttyOdd1) === 0 ? '-' : formatNumber(symbolInfo.bidQttyOdd1)} fireEvent={_fireEvent} />
                            </td>
                            <td>
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.offerPriceOdd1_color} id={symbolInfo.code + "_offerPriceOdd1"} value={priceToNumber(symbolInfo.offerPriceOdd1) === 0 ? '-' : formatNumber_N1(symbolInfo.offerPriceOdd1)} fireEvent={_fireEvent} />
                                <span> | </span>
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_offerQttyOdd1"} value={priceToNumber(symbolInfo.offerQttyOdd1) === 0 ? '-' : formatNumber(symbolInfo.offerQttyOdd1)} fireEvent={_fireEvent} />
                            </td>
                        </tr>
                    </tbody>
                    </>
                ) : (
                    <>
                    <tbody>
                        <tr>
                            <td colSpan={2} className={symbolInfo.matchPrice_color}>
                                <div style={{width: '100%', display: 'flex', alignItems: 'center'}} >
                                    {_renderHeaderBox()}
                                    <span id={symbolInfo.code + "_code"} style={{cursor: 'pointer', fontWeight: 'bolder'}}>{symbolInfo.code}{(symbolInfo.referenceStatus && symbolInfo.referenceStatus !== '' && symbolInfo.referenceStatus !== '0' ? "*" : "")}</span>
                                    <div className='flex-1 text-right'>
                                        <HighlightSpan style={{fontWeight: 'bolder',fontSize: '18px',lineHeight: '21px'}} color={symbolInfo.matchPrice_color} id={symbolInfo.code + "_matchPrice"} value={symbolInfo.matchPrice} fireEvent={_fireEvent} /> 
                                        <span className="fs-14"> (</span>
                                        <HighlightSpan style={{fontSize: '14px',lineHeight: '18px'}} color={symbolInfo.matchPrice_color} id={symbolInfo.code + "_changePrice"} value={formatNumber_N1(symbolInfo.changePrice)} fireEvent={false} />
                                        <span className="fs-14">/</span>
                                        <HighlightSpan style={{fontSize: '14px',lineHeight: '18px'}} color={symbolInfo.matchPrice_color} id={symbolInfo.code + "_pctChangePrice"} value={formatNumber_N1(symbolInfo.pctChangePrice)} fireEvent={false} />
                                        <span className="fs-14">%)</span>
                                    </div> 
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span className="cl_flatlist">{t("tongKLKhop")}: </span> 
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_totalTradedQttyNM"} value={priceToNumber(symbolInfo.totalTradedQttyNM) === 0 ? '-' : formatQtty(symbolInfo.totalTradedQttyNM)} fireEvent={_fireEvent} />
                            </td>
                            <td>
                                <span className="cl_flatlist">{t("klKhop")}: </span> 
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.matchPrice_color}  id={symbolInfo.code + "_matchQtty"} value={priceToNumber(symbolInfo.matchQtty) === 0 ? '-' : formatQtty(symbolInfo.matchQtty)} fireEvent={_fireEvent} />
                            </td>
                        </tr>
                        <tr>
                            <td className="cl_flatlist">{t("muaBid")}</td>
                            <td className="cl_flatlist">{t("banAsk")}</td>
                        </tr>
                        <tr>
                            <td>
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.bidPrice1_color} id={symbolInfo.code + "_bidPrice1"} value={priceToNumber(symbolInfo.bidPrice1) === 0 ? '-' : formatNumber_N1(symbolInfo.bidPrice1)} fireEvent={_fireEvent} />
                                <span> | </span>
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_bidQtty1"} value={priceToNumber(symbolInfo.bidQtty1) === 0 ? '-' : formatQtty(symbolInfo.bidQtty1)} fireEvent={_fireEvent} />
                            </td>
                            <td>
                                <HighlightSpan style={{fontWeight: 'bolder'}} color={symbolInfo.offerPrice1_color} id={symbolInfo.code + "_offerPrice1"} value={priceToNumber(symbolInfo.offerPrice1) === 0 ? '-' : formatNumber_N1(symbolInfo.offerPrice1)} fireEvent={_fireEvent} />
                                <span> | </span>
                                <HighlightSpan style={{fontWeight: 'bolder'}} id={symbolInfo.code + "_offerQtty1"} value={priceToNumber(symbolInfo.offerQtty1) === 0 ? '-' :formatQtty(symbolInfo.offerQtty1)} fireEvent={_fireEvent} />
                            </td>
                        </tr>
                    </tbody>
                    </>
                )}
            </table>
        </div>            
    )
});